import { Head, Link } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';

interface PoliciesProps {
  currentUrl: string;
}

const ShieldIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const RefreshIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);

const FileTextIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const ArrowRightIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const policies = [
  {
    href: '/politica-de-privacidad',
    icon: ShieldIcon,
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    title: 'Política de Privacidad',
    description: 'Cómo recopilamos, usamos y protegemos tu información personal.',
  },
  {
    href: '/politica-de-devolucion',
    icon: RefreshIcon,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    title: 'Política de Devolución',
    description: 'Garantía de calidad, plazos y métodos de reembolso para productos frescos.',
  },
  {
    href: '/terminos-de-servicio',
    icon: FileTextIcon,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    title: 'Términos y Condiciones',
    description: 'Condiciones generales de uso, pedidos, pagos y entregas.',
  },
];

export default function PoliciesIndex({ currentUrl }: PoliciesProps) {
  const pageTitle = 'Políticas | Abasto Virtual Córdoba';
  const pageDescription = 'Consultá nuestras políticas de privacidad, devolución y términos y condiciones de uso.';

  return (
    <MainLayout>
      <Head title={pageTitle}>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <link rel="canonical" href={currentUrl} />
      </Head>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-16 pt-8 md:pt-12">
        <div className="mx-auto max-w-3xl px-4">
          {/* Header */}
          <div className="mb-10 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100">
              <FileTextIcon className="h-8 w-8 text-slate-600" />
            </div>
            <h1 className="mb-3 text-3xl font-bold text-gray-900 md:text-4xl">
              Políticas
            </h1>
            <p className="text-lg text-gray-500">
              Transparencia y confianza en cada compra
            </p>
          </div>

          {/* Policy cards */}
          <div className="space-y-4">
            {policies.map((policy) => (
              <Link
                key={policy.href}
                href={policy.href}
                className="group flex items-center gap-5 rounded-2xl border border-gray-200 bg-white p-6 transition-all hover:border-gray-300 hover:shadow-md"
              >
                <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ${policy.iconBg}`}>
                  <policy.icon className={`h-6 w-6 ${policy.iconColor}`} />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-gray-900">{policy.title}</h2>
                  <p className="mt-1 text-sm text-gray-500">{policy.description}</p>
                </div>
                <ArrowRightIcon className="h-5 w-5 flex-shrink-0 text-gray-300 transition-colors group-hover:text-gray-500" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
